import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { adminApi } from "../../api/admin";
import type { AdminUserRow } from "../../api/types";
import { formatDateTime } from "../../shared/format";
import { Badge, Button, Card, DataTable, Input, Spinner } from "../../ui";

const columns: ColumnDef<AdminUserRow, unknown>[] = [
  { accessorKey: "email", header: "Email" },
  {
    id: "name",
    header: "Name",
    cell: ({ row }) =>
      [row.original.first_name, row.original.last_name]
        .filter(Boolean)
        .join(" "),
  },
  {
    id: "created_at",
    header: "Created",
    cell: ({ row }) => formatDateTime(row.original.created_at),
  },
  {
    id: "last_login_at",
    header: "Last login",
    cell: ({ row }) => formatDateTime(row.original.last_login_at),
  },
  {
    id: "products",
    header: "Products",
    cell: ({ row }) => row.original.products.join(", ") || "—",
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => (
      <Badge
        tone={
          row.original.status === "active"
            ? "success"
            : row.original.status === "suspended"
              ? "warning"
              : "danger"
        }
      >
        {row.original.status}
      </Badge>
    ),
  },
];

export function UsersListPage() {
  const navigate = useNavigate();
  // The workspace decides the product; the URL says which one (docs/14 §1).
  const { product } = useOutletContext<{ product: string | null }>();
  const [draft, setDraft] = useState({ q: "", status: "" });
  const [filters, setFilters] = useState(draft);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const cursor = cursors[cursors.length - 1];
  const query = useQuery({
    queryKey: ["admin", "users", product, filters, cursor],
    queryFn: () =>
      adminApi.users({ ...filters, product: product ?? undefined, cursor }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFilters(draft);
    setCursors([undefined]);
  };
  return (
    <Card>
      <h1 className="mb-4 text-xl font-semibold">Users</h1>
      <form
        onSubmit={submit}
        className="mb-4 grid gap-3 md:grid-cols-4"
        aria-label="Search users"
      >
        <Input
          label="Search"
          placeholder="email, name, user id, org id"
          value={draft.q}
          onChange={(e) => setDraft({ ...draft, q: e.target.value })}
        />
        <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
          Status
          <select
            className="rounded-md border border-slate-300 px-3 py-2 text-sm font-normal"
            value={draft.status}
            onChange={(e) => setDraft({ ...draft, status: e.target.value })}
          >
            <option value="">any</option>
            <option value="active">active</option>
            <option value="suspended">suspended</option>
            <option value="deleted">deleted</option>
          </select>
        </label>
        <div className="flex items-end">
          <Button type="submit">Search</Button>
        </div>
      </form>
      {query.isLoading ? <Spinner /> : null}
      {query.data ? (
        <DataTable<AdminUserRow>
          columns={columns}
          data={query.data.items}
          onRowClick={(row) =>
            navigate(
              product
                ? `/admin/p/${product}/users/${row.id}`
                : `/admin/platform/users/${row.id}`,
            )
          }
          emptyMessage="No users match."
        />
      ) : null}
      <div className="mt-4 flex gap-2">
        <Button
          variant="secondary"
          disabled={cursors.length < 2}
          onClick={() => setCursors(cursors.slice(0, -1))}
        >
          Previous
        </Button>
        <Button
          variant="secondary"
          disabled={!query.data?.next_cursor}
          onClick={() =>
            setCursors([...cursors, query.data?.next_cursor ?? undefined])
          }
        >
          Next
        </Button>
      </div>
    </Card>
  );
}
